import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { getIsTodayCreated, getIsTodayStudied } from '@util/workbook';
import { EWorkbookStudyType } from '@datatype/iyagigo/type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import {
  useAttendanceQuery,
  useCheckStudentAttendanceMutation,
  useCreateStudyRecordMutation,
  useModifyStudentMutation,
  useReadMyStudyRecordsQuery,
  useStudentKeyQuery,
} from '@hook/iyagigo/useApi';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import { useStudentKey } from '@hook/iyagigo/useInit';
import { actions as studentActions } from '@store/slice/student-slice';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { useLocation, useNavigate } from 'react-router-dom';
import { createTestPath, IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { IS_DEMO } from '@constant/variable';

import {
  EApiAttendanceType,
  EApiLanguageCode,
  EApiStudyType,
  ICreateStudyRecordRequest,
  IVocabularyWorkbookData,
  IWorkbookData,
} from '@datatype/iyagigo/api-type';
import { getAttendanceType, getHasDateAttendance } from '@util/attendance';

/**
 * 회원/비회원 상관없이 studyConfiguration을 조회하는 훅
 */
export const useStudyConfiguration = () => {
  const studentQuery = useStudentKeyQuery();
  return studentQuery.data?.data?.studyConfiguration;
};

/**
 * 학습언어 전환 훅
 *
 * 학습언어를 바꾸면 메뉴 구성 전체가 갈리므로 학습 중에는 노출하지 않는다.
 * (홈에서만 호출 — 학습 페이지 진입 시에만 isStudying이 true가 된다)
 *
 * @returns changeStudyLanguage - 학습언어 전환 핸들러
 */
export const useChangeStudyLanguage = () => {
  const dispatch = useTypedDispatch();
  const mutation = useModifyStudentMutation();

  const changeStudyLanguage = (language: EApiLanguageCode) => {
    dispatch(studentActions.storeCurrentStudyLanguage(language));
    const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
    if (!studentKey) return;
    mutation.mutate({ studentKey, studyLanguage: language });
  };

  return changeStudyLanguage;
};

export const useOpenAttendance = () => {
  const attendanceRecord = useTypedSelector((state) => state.rootReducer?.studentReducer?.attendance) ?? {};
  const dispatch = useTypedDispatch();
  const mutation = useCheckStudentAttendanceMutation();
  // IyagigoApp mount 직후 바로 실행되는 OPEN 출석은 /student/me → localStorage 동기화보다 먼저 실행될 수 있다.
  // race condition으로 이전 localStorage studentKey를 읽지 않도록 useStudentKey의 resolved key를 사용한다.
  const { studentKey, studentKeySyncing } = useStudentKey();
  const attendanceType = EApiAttendanceType.OPEN;
  const now = new Date();
  const today = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  const lastAttended = attendanceRecord[attendanceType] ?? '';
  const lastAttendedDate = lastAttended
    ? `${new Date(lastAttended).getFullYear()}-${new Date(lastAttended).getMonth() + 1}-${new Date(lastAttended).getDate()}`
    : '';

  useEffect(() => {
    (async () => {
      // TODO: attendanceRecord가 studentKey별로 분리되어 있지 않아 studentKey 변경 시 오판할 수 있다.
      // if (today === lastAttendedDate) return;
      if (studentKeySyncing) return;
      if (!studentKey) return;
      await mutation.mutateAsync({ studentKey, attendedAt: now.toISOString(), attendanceType });
      dispatch(studentActions.checkAttendance({ attendanceType, date: today }));
    })();
  }, [today, lastAttendedDate, studentKey, studentKeySyncing]);

  return;
};

export const useCheckAttendance = () => {
  const attendanceRecord = useTypedSelector((state) => state.rootReducer?.studentReducer?.attendance) ?? {};
  const dispatch = useTypedDispatch();
  const mutation = useCheckStudentAttendanceMutation();
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';

  const checkAttendance = (attendanceType: EApiAttendanceType) => {
    const now = new Date();
    const today = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
    const lastAttended = attendanceRecord[attendanceType] ?? '';
    const lastAttendedDate = lastAttended
      ? `${new Date(lastAttended).getFullYear()}-${new Date(lastAttended).getMonth() + 1}-${new Date(lastAttended).getDate()}`
      : '';

    // TODO: attendanceRecord가 studentKey별로 분리되어 있지 않아 studentKey 변경 시 오판할 수 있다.
    // if (today === lastAttendedDate) return;
    if (!studentKey) return;
    mutation.mutateAsync({ studentKey, attendedAt: now.toISOString(), attendanceType });
    dispatch(studentActions.checkAttendance({ attendanceType, date: today }));
  };

  return checkAttendance;
};

export const useRecordStudy = () => {
  const mutation = useCreateStudyRecordMutation();
  // useApiMutation의 mutate는 렌더마다 바뀌므로 최신 mutation을 참조한다.
  // 반환 함수는 고정해 강의 화면의 자동 저장 타이머가 재시작되지 않게 한다.
  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;
  const checkStudyRecord = useCallback((request: ICreateStudyRecordRequest) => {
    if (!request.studentKey) return;
    mutationRef.current.mutate(request);
  }, []);
  return checkStudyRecord;
};

/**
 * 연속 출석일을 계산하는 커스텀 훅
 *
 * @returns 연속 출석일 수 (오늘 또는 어제부터 시작해서 연속된 날짜 수)
 */
export const useConsecutiveAttendance = () => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const mainMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.mainMenu) as
    | string
    | null;
  const subMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu) as string | null;
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const attendanceType = getAttendanceType(
    studyLanguage,
    mainMenu === 'vocabulary-book' ? 'vocabulary-book' : subMenu,
    mainMenu,
  ); // TODO: 단어집도 서브메뉴 만들기..
  const attendanceQuery = useAttendanceQuery(studentKey, attendanceType, { enabled: !!isAuthenticated });
  const attendances = attendanceQuery.data?.data || [];

  return useMemo(() => {
    if (!isAuthenticated) return 0;
    if (attendances.length === 0) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 출석 날짜들을 Date 객체로 변환하고 정렬 (최신순)
    const sortedDates = attendances
      .map((att) => {
        const d = new Date(att.attendedAt);
        d.setHours(0, 0, 0, 0);
        return d;
      })
      .sort((a: Date, b: Date) => b.getTime() - a.getTime());

    // 오늘 출석했는지 확인
    const todayAttended = sortedDates.some((d: Date) => d.getTime() === today.getTime());

    // 오늘 출석 안했으면 연속일수 0
    if (!todayAttended) return 0;

    // 오늘부터 연속일 계산
    let count = 0;
    const checkDate = new Date(today);

    for (const attDate of sortedDates) {
      if (attDate.getTime() === checkDate.getTime()) {
        count++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else if (attDate.getTime() < checkDate.getTime()) {
        // 연속이 끊김
        break;
      }
    }

    return count;
  }, [isAuthenticated, attendances]);
};

/**
 * 주간 출석 데이터를 계산하는 커스텀 훅
 *
 * @returns 월요일부터 일요일까지의 출석 상태 배열
 * - label: 요일 이름 (월, 화, 수, 목, 금, 토, 일)
 * - isCheck: 해당 날짜에 출석했는지 여부
 * - isCurrent: 오늘 날짜인지 여부
 * - isFuture: 미래 날짜인지 여부
 */
export const useWeekAttendance = () => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const mainMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.mainMenu) as
    | string
    | null;
  const subMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu) as string | null;
  const attendanceType = getAttendanceType(
    studyLanguage,
    mainMenu === 'vocabulary-book' ? 'vocabulary-book' : subMenu,
    mainMenu,
  );
  const attendanceQuery = useAttendanceQuery(studentKey, attendanceType, { enabled: !!isAuthenticated });
  const studentQuery = useStudentKeyQuery();
  const attendances = attendanceQuery.data?.data || [];

  const WEEK_DAYS = ['월', '화', '수', '목', '금', '토', '일'];

  return useMemo(() => {
    // 가입일
    const createdAt = studentQuery.data?.data?.createdAt;
    const signupDate = createdAt ? new Date(createdAt) : null;
    if (signupDate) signupDate.setHours(0, 0, 0, 0);

    // 이번주 월요일 계산
    const today = new Date();
    const currentDay = today.getDay(); // 0 (일요일) ~ 6 (토요일)
    const mondayDiff = currentDay === 0 ? 6 : currentDay - 1;
    const monday = new Date(today);
    monday.setDate(today.getDate() - mondayDiff);
    monday.setHours(0, 0, 0, 0);

    // 미인증 시 요일만 표시 (O/X 없음)
    if (!isAuthenticated) {
      return WEEK_DAYS.map((label, index) => {
        const targetDate = new Date(monday);
        targetDate.setDate(monday.getDate() + index);
        const isCurrent = targetDate.toDateString() === today.toDateString();
        return { label, isCheck: false, isCurrent, isFuture: true };
      });
    }

    return WEEK_DAYS.map((item, index) => {
      // 해당 요일의 날짜 계산 (월요일 + index일)
      const targetDate = new Date(monday);
      targetDate.setDate(monday.getDate() + index);

      // 출석 데이터에서 해당 날짜가 있는지 확인
      const isCheck = getHasDateAttendance(attendances, targetDate);

      // 오늘 날짜인지 확인
      const isCurrent = targetDate.toDateString() === today.toDateString();
      const isFuture = targetDate > today;
      const isBeforeSignup = signupDate ? targetDate < signupDate : false;

      return { label: item, isCheck, isCurrent, isFuture, isBeforeSignup };
    });
  }, [isAuthenticated, attendances, studentQuery.data?.data?.createdAt]);
};

/**
 * 학습 통계를 계산하는 커스텀 훅
 *
 * @param type - 'week' (이번주) 또는 'month' (이번달)
 * @returns 학습한 단어, 문법, 시험 문제 수
 * - vocabularyCount: 학습한 단어 개수
 * - grammarCount: 학습한 문법 개수
 * - testCount: 학습한 시험 문제 수
 * - totalCount: 전체 학습 문제 수
 */
export const useStudyStatistic = (type: 'week' | 'month' = 'week') => {
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const studyRecordQuery = useReadMyStudyRecordsQuery(
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
  );

  return useMemo(() => {
    const records = studyRecordQuery.data?.data || [];
    const today = new Date();

    let startDate: Date;

    if (type === 'week') {
      // 이번주 월요일 계산
      const currentDay = today.getDay();
      const mondayDiff = currentDay === 0 ? 6 : currentDay - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - mondayDiff);
    } else {
      // 이번달 1일 계산
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    }
    startDate.setHours(0, 0, 0, 0);

    // 기간 데이터 필터링
    const filteredRecords = records.filter((record) => {
      const recordDate = new Date(record.studyDate);
      return recordDate >= startDate && recordDate <= today;
    });

    // studyType별 집계
    const vocabularyCount = filteredRecords
      .filter((r) => r.studyType === 'vocabulary')
      .reduce((sum, r) => sum + r.studyCount, 0);

    const grammarCount = filteredRecords
      .filter((r) => r.studyType === 'grammar')
      .reduce((sum, r) => sum + r.studyCount, 0);

    const testCount = filteredRecords.filter((r) => r.studyType === 'test').reduce((sum, r) => sum + r.studyCount, 0);

    const totalCount = vocabularyCount + grammarCount + testCount;

    return {
      vocabularyCount,
      grammarCount,
      testCount,
      totalCount,
    };
  }, [studyRecordQuery.data?.data, studyLanguage, type]);
};

/** @deprecated useStudyStatistic('week') 사용 */
export const useWeeklyStudyStatistic = () => useStudyStatistic('week');

export const useBeginStudy = () => {
  const navigate = useNavigate();
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const query = useStudentKeyQuery();

  if (!isAuthenticated) return () => navigate(createTestPath('problem'));

  const currentTestCodes = query.data?.data?.studyConfiguration?.testCodes || [];
  const currentSubjects = currentTestCodes.map((test) => {
    switch (test) {
      case 'T1100':
        return 'jlpt1';
      case 'T1200':
        return 'jlpt2';
      case 'T1300':
        return 'jlpt3';
      case 'T1400':
        return 'jlpt4';
      case 'T1500':
        return 'jlpt5';
      default:
        return '';
    }
  }) as string[];

  return () => navigate(createTestPath('home'));
};

// TODO: Refactoring
export const useStudyConfigurationSync = () => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const studyConfiguration = useStudyConfiguration();
  const isLandingPage =
    location.pathname === IYAGIGO_PATH.LANDING_PATH || location.pathname === `${IYAGIGO_PATH.LANDING_PATH}/`;

  // 회원/비회원 공통: server studyConfiguration 동기화
  useEffect(() => {
    // landing page면 '학습언어 선택 Flow' -> X
    if (isLandingPage) {
      dispatch(modalActions.hideOverlay());
      return;
    }

    const serverInitialStudyLanguage = studyConfiguration?.studyLanguage;

    if (!serverInitialStudyLanguage) {
      // '서버에 설정된 학습언어'가 없으면 -> 학습언어 말풍선
      dispatch(modalActions.showCallout());
      return;
    }

    // '서버에 설정된 학습언어'가 있으면 '현재학습언어'로 설정
    dispatch(
      studentActions.storeCurrentStudyLanguage(
        serverInitialStudyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
      ),
    );
    dispatch(studentActions.storeCurrentEnglishVocabularyLevel(studyConfiguration.englishVocabularyClozeLevel));
    dispatch(studentActions.storeCurrentEnglishGrammarLevel(studyConfiguration.englishGrammarClozeLevel));
    dispatch(studentActions.storeCurrentJapaneseVocabularyLevel(studyConfiguration.japaneseVocabularyClozeLevel));
    dispatch(studentActions.storeCurrentJapaneseGrammarLevel(studyConfiguration.japaneseGrammarClozeLevel));
    dispatch(studentActions.storeCurrentToeicTest(studyConfiguration.toeicTestLevel));
    dispatch(studentActions.storeCurrentJlptTest(studyConfiguration.jlptTestLevel));
    dispatch(
      studentActions.storeCurrentEnglishTestVocabularyBookLevel(studyConfiguration.englishTestVocabularyBookLevel),
    );
    dispatch(
      studentActions.storeCurrentEnglishStudyVocabularyBookLevel(studyConfiguration.englishStudyVocabularyBookLevel),
    );
    dispatch(
      studentActions.storeCurrentJapaneseTestVocabularyBookLevel(studyConfiguration.japaneseTestVocabularyBookLevel),
    );
    dispatch(
      studentActions.storeCurrentJapaneseStudyVocabularyBookLevel(studyConfiguration.japaneseStudyVocabularyBookLevel),
    );
  }, [isLandingPage, studyConfiguration]);

  return;
};

export const useWorkbookSync = (data: IWorkbookData[]) => {
  const dispatch = useTypedDispatch();
  const [workbookIdx, setWorkbookIdx] = useState(0);
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const clozeWorkbookStudyRecord =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.clozeWorkbookStudyRecord) || {};
  const workbookStudyType =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.workbookStudyType) ??
    EWorkbookStudyType.TODAY_STUDY;
  const totalWorkbookCount = data.length;

  // ############### Sync Server Data to Redux ###############
  useEffect(() => {
    if (totalWorkbookCount > 0) {
      const records = data.map((item: IWorkbookData) => ({
        key: item.key,
        isCorrect: item?.isCorrect ?? null,
        todayStudiedAt: item?.todayStudiedAt ?? null,
      }));
      dispatch(workbookActions.syncClozeStudyRecord({ records, isAuthenticated: isAuthenticated ?? false }));
    }
  }, [totalWorkbookCount, dispatch, isAuthenticated]); // Sync whenever data loads/changes

  // ############### Calculate Progress & Initial Index ###############
  const isInitialized = React.useRef(false);

  const studiedWorkbookCount = useMemo(() => {
    if (totalWorkbookCount === 0) return 0;

    return data.filter((item) => {
      const record = clozeWorkbookStudyRecord[item.key];
      return record && getIsTodayStudied(record.todayStudiedAt ?? '');
    }).length;
  }, [data, isAuthenticated, clozeWorkbookStudyRecord]);

  useEffect(() => {
    if (isInitialized.current) return;
    if (totalWorkbookCount === 0) return;

    const isRestartStudyType =
      workbookStudyType === EWorkbookStudyType.TODAY_REVIEW ||
      workbookStudyType === EWorkbookStudyType.BOOKMARK_NOTE ||
      workbookStudyType === EWorkbookStudyType.INCORRECT_NOTE;
    const nextIndex = isRestartStudyType ? 0 : Math.min(studiedWorkbookCount, totalWorkbookCount - 1);
    setWorkbookIdx(nextIndex);
    isInitialized.current = true;
  }, [studiedWorkbookCount, totalWorkbookCount]);

  const isProgress = studiedWorkbookCount > 0;
  const isComplete = studiedWorkbookCount >= totalWorkbookCount;

  const reviewWorkbook = data.filter((item) => getIsTodayCreated(item.publishedAt) && item.lastStudiedAt);
  const newWorkbook = data.filter((item) => getIsTodayCreated(item.publishedAt) && !item.lastStudiedAt);

  return {
    workbookIdx,
    setWorkbookIdx,
    studiedWorkbookCount,
    totalWorkbookCount,
    isProgress,
    isComplete,
    reviewWorkbook,
    newWorkbook,
  };
};

// 단어집 학습 진입 시 시작 위치 계산 (cloze useWorkbookSync 패턴 미러).
// 백엔드가 `studiedAt ASC NULLS LAST`로 정렬해서 반환 — 푼 단어 앞쪽, 안 푼 단어 뒤쪽.
// - 처음/이어서: studiedCount 위치 = 첫 미학습 단어
// - 복습 모드(전부 studiedAt 있음): 0번부터 (가장 오래 안 본 단어)
export const useVocabularyWorkbookSync = (data: IVocabularyWorkbookData[]) => {
  const [workbookIdx, setWorkbookIdx] = useState(0);
  const totalWorkbookCount = data.length;

  // ############### Calculate Progress & Initial Index ###############
  const isInitialized = React.useRef(false);
  const [isReady, setIsReady] = useState(false);

  const studiedWorkbookCount = useMemo(() => {
    if (totalWorkbookCount === 0) return 0;
    return data.filter((item) => !!item.studiedAt).length;
  }, [data]);

  useEffect(() => {
    if (isInitialized.current) return;
    if (totalWorkbookCount === 0) return;

    // 복습 모드: 모두 학습 완료 → 0번부터 restart
    const isRestartStudyType = studiedWorkbookCount >= totalWorkbookCount;
    const nextIndex = isRestartStudyType ? 0 : Math.min(studiedWorkbookCount, totalWorkbookCount - 1);
    setWorkbookIdx(nextIndex);
    isInitialized.current = true;
    setIsReady(true);
  }, [studiedWorkbookCount, totalWorkbookCount]);

  const isProgress = studiedWorkbookCount > 0;
  const isComplete = studiedWorkbookCount >= totalWorkbookCount;

  return {
    workbookIdx,
    setWorkbookIdx,
    studiedWorkbookCount,
    totalWorkbookCount,
    isProgress,
    isComplete,
    isReady,
  };
};

const useHomeAttendances = () => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isEnglish = studyLanguage === EApiLanguageCode.ENGLISH;

  const characterType = isEnglish
    ? EApiAttendanceType.ENGLISH_CLASSROOM_CHARACTER
    : EApiAttendanceType.JAPANESE_CLASSROOM_CHARACTER;
  const classroomGrammarType = isEnglish
    ? EApiAttendanceType.ENGLISH_CLASSROOM_GRAMMAR
    : EApiAttendanceType.JAPANESE_CLASSROOM_GRAMMAR;
  const basicExpressionType = isEnglish
    ? EApiAttendanceType.ENGLISH_CLASSROOM_EXPRESSION
    : EApiAttendanceType.JAPANESE_CLASSROOM_EXPRESSION;
  const conversationType = isEnglish
    ? EApiAttendanceType.ENGLISH_CLASSROOM_CONVERSATION
    : EApiAttendanceType.JAPANESE_CLASSROOM_CONVERSATION;
  const speakingType = isEnglish ? EApiAttendanceType.ENGLISH_SPEAKING : EApiAttendanceType.JAPANESE_SPEAKING;
  const vocabularyType = isEnglish ? EApiAttendanceType.ENGLISH_VOCABULARY : EApiAttendanceType.JAPANESE_VOCABULARY;
  const grammarType = isEnglish ? EApiAttendanceType.ENGLISH_GRAMMAR : EApiAttendanceType.JAPANESE_GRAMMAR;
  const expressionType = isEnglish
    ? EApiAttendanceType.ENGLISH_AUTHENTIC_EXPRESSION
    : EApiAttendanceType.JAPANESE_AUTHENTIC_EXPRESSION;
  const storyType = isEnglish ? EApiAttendanceType.ENGLISH_STORY : EApiAttendanceType.JAPANESE_STORY;
  const bookType = isEnglish ? EApiAttendanceType.ENGLISH_VOCABULARY_BOOK : EApiAttendanceType.JAPANESE_VOCABULARY_BOOK;

  // 기존에는 서비스별 학습 출석을 합쳐 Home 출석으로 사용했으나,
  // 학습 완료 여부와 앱 방문 여부를 분리하기 위해 주간 스탬프와 연속 출석은 OPEN 출석만 사용한다.
  const openQuery = useAttendanceQuery(studentKey, EApiAttendanceType.OPEN, { enabled: !!studentKey });
  // 서비스별 출석은 오늘의 학습 경로 완료 상태에 사용
  const characterQuery = useAttendanceQuery(studentKey, characterType, { enabled: !!studentKey });
  const classroomGrammarQuery = useAttendanceQuery(studentKey, classroomGrammarType, { enabled: !!studentKey });
  const basicExpressionQuery = useAttendanceQuery(studentKey, basicExpressionType, { enabled: !!studentKey });
  const conversationQuery = useAttendanceQuery(studentKey, conversationType, { enabled: !!studentKey });
  const speakingQuery = useAttendanceQuery(studentKey, speakingType, { enabled: !!studentKey });
  const vocabularyQuery = useAttendanceQuery(studentKey, vocabularyType, { enabled: !!studentKey });
  const grammarQuery = useAttendanceQuery(studentKey, grammarType, { enabled: !!studentKey });
  const expressionQuery = useAttendanceQuery(studentKey, expressionType, { enabled: !!studentKey });
  const storyQuery = useAttendanceQuery(studentKey, storyType, { enabled: !!studentKey });
  const bookQuery = useAttendanceQuery(studentKey, bookType, { enabled: !!studentKey });

  return useMemo(() => {
    const openAttendances = openQuery.data?.data ?? [];
    const characterAttendances = characterQuery.data?.data ?? [];
    const classroomGrammarAttendances = classroomGrammarQuery.data?.data ?? [];
    const basicExpressionAttendances = basicExpressionQuery.data?.data ?? [];
    const conversationAttendances = conversationQuery.data?.data ?? [];
    const speakingAttendances = speakingQuery.data?.data ?? [];
    const vocabularyClozeAttendances = vocabularyQuery.data?.data ?? [];
    const grammarClozeAttendances = grammarQuery.data?.data ?? [];
    const expressionAttendances = expressionQuery.data?.data ?? [];
    const storyAttendances = storyQuery.data?.data ?? [];
    const bookAttendances = bookQuery.data?.data ?? [];
    const all = [...openAttendances];
    // const all = [
    //   ...characterAttendances,
    //   ...classroomGrammarAttendances,
    //   ...vocabularyClozeAttendances,
    //   ...grammarClozeAttendances,
    //   ...expressionAttendances,
    //   ...storyAttendances,
    //   ...bookAttendances,
    // ];
    const dateMap = new Map<string, { attendedAt: string }>();
    for (const att of all) {
      const d = new Date(att.attendedAt);
      d.setHours(0, 0, 0, 0);
      const key = d.toISOString();
      if (!dateMap.has(key)) dateMap.set(key, { attendedAt: att.attendedAt });
    }
    return {
      attendances: Array.from(dateMap.values()),
      characterAttendances,
      basicExpressionAttendances,
      conversationAttendances,
      speakingAttendances,
      grammarAttendances: classroomGrammarAttendances,
      clozeAttendances: [...vocabularyClozeAttendances, ...grammarClozeAttendances],
      vocabularyBookAttendances: bookAttendances,
      miniStoryAttendances: storyAttendances,
      authenticExpressionAttendances: expressionAttendances,
    };
  }, [
    openQuery.data,
    characterQuery.data,
    classroomGrammarQuery.data,
    basicExpressionQuery.data,
    conversationQuery.data,
    speakingQuery.data,
    vocabularyQuery.data,
    grammarQuery.data,
    expressionQuery.data,
    storyQuery.data,
    bookQuery.data,
  ]);
};

/**
 * 홈(Hub)용 총 출석일 — 앱 OPEN 출석 기준
 *
 * useHomeAttendances가 날짜 단위로 중복 제거하므로, 하루에 여러 번 들어와도 1일로 센다.
 */
export const useHomeAttendanceDayCount = () => {
  const { attendances } = useHomeAttendances();
  return attendances.length;
};

/**
 * 홈(Hub)용 연속 출석일 — 앱 OPEN 출석 기준
 */
export const useHomeConsecutiveAttendance = () => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const { attendances } = useHomeAttendances();

  return useMemo(() => {
    // if (!isAuthenticated) return 0;
    if (attendances.length === 0) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sortedDates = attendances
      .map((att) => {
        const d = new Date(att.attendedAt);
        d.setHours(0, 0, 0, 0);
        return d;
      })
      .sort((a: Date, b: Date) => b.getTime() - a.getTime());

    const todayAttended = sortedDates.some((d: Date) => d.getTime() === today.getTime());
    if (!todayAttended) return 0;

    let count = 0;
    const checkDate = new Date(today);
    for (const attDate of sortedDates) {
      if (attDate.getTime() === checkDate.getTime()) {
        count++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else if (attDate.getTime() < checkDate.getTime()) {
        break;
      }
    }
    return count;
  }, [attendances]);
};

/**
 * 홈(Hub)용 주간 출석 — 스탬프는 앱 OPEN, 학습 경로 완료는 서비스별 출석 기준
 */
export const useHomeWeekAttendance = () => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studentQuery = useStudentKeyQuery();
  const {
    attendances,
    characterAttendances,
    basicExpressionAttendances,
    conversationAttendances,
    speakingAttendances,
    grammarAttendances,
    clozeAttendances,
    vocabularyBookAttendances,
    miniStoryAttendances,
    authenticExpressionAttendances,
  } = useHomeAttendances();
  const WEEK_DAYS = ['월', '화', '수', '목', '금', '토', '일'];

  return useMemo(() => {
    if (IS_DEMO) {
      const checkedDayIndexes = new Set([0, 1, 3, 4, 6]);

      return WEEK_DAYS.map((label, index) => ({
        label,
        isCheck: checkedDayIndexes.has(index),
        isCharacterCheck: false,
        isClassroomExpressionCheck: false,
        isConversationCheck: false,
        isSpeakingCheck: false,
        isGrammarCheck: false,
        isClozeCheck: false,
        isVocabularyBookCheck: false,
        isMiniStoryCheck: false,
        isAuthenticExpressionCheck: false,
        isCurrent: index === 6,
        isFuture: false,
        isBeforeSignup: false,
      }));
    }

    const createdAt = studentQuery.data?.data?.createdAt;
    const signupDate = createdAt ? new Date(createdAt) : null;
    if (signupDate) signupDate.setHours(0, 0, 0, 0);

    const today = new Date();
    const currentDay = today.getDay();
    const mondayDiff = currentDay === 0 ? 6 : currentDay - 1;
    const monday = new Date(today);
    monday.setDate(today.getDate() - mondayDiff);
    monday.setHours(0, 0, 0, 0);

    // if (!isAuthenticated) {
    //   return WEEK_DAYS.map((label, index) => {
    //     const targetDate = new Date(monday);
    //     targetDate.setDate(monday.getDate() + index);
    //     const isCurrent = targetDate.toDateString() === today.toDateString();
    //     return { label, isCheck: false, isCurrent, isFuture: true };
    //   });
    // }

    return WEEK_DAYS.map((item, index) => {
      const targetDate = new Date(monday);
      targetDate.setDate(monday.getDate() + index);

      const isCharacterCheck = getHasDateAttendance(characterAttendances, targetDate);
      const isClassroomExpressionCheck = getHasDateAttendance(basicExpressionAttendances, targetDate);
      const isConversationCheck = getHasDateAttendance(conversationAttendances, targetDate);
      const isSpeakingCheck = getHasDateAttendance(speakingAttendances, targetDate);
      const isGrammarCheck = getHasDateAttendance(grammarAttendances, targetDate);
      const isClozeCheck = getHasDateAttendance(clozeAttendances, targetDate);
      const isVocabularyBookCheck = getHasDateAttendance(vocabularyBookAttendances, targetDate);
      const isMiniStoryCheck = getHasDateAttendance(miniStoryAttendances, targetDate);
      const isAuthenticExpressionCheck = getHasDateAttendance(authenticExpressionAttendances, targetDate);
      const isCheck = getHasDateAttendance(attendances, targetDate);

      const isCurrent = targetDate.toDateString() === today.toDateString();
      const isFuture = targetDate > today;
      const isBeforeSignup = signupDate ? targetDate < signupDate : false;

      return {
        label: item,
        isCheck,
        isCharacterCheck,
        isClassroomExpressionCheck,
        isConversationCheck,
        isSpeakingCheck,
        isGrammarCheck,
        isClozeCheck,
        isVocabularyBookCheck,
        isMiniStoryCheck,
        isAuthenticExpressionCheck,
        isCurrent,
        isFuture,
        isBeforeSignup,
      };
    });
  }, [
    attendances,
    characterAttendances,
    basicExpressionAttendances,
    conversationAttendances,
    speakingAttendances,
    grammarAttendances,
    clozeAttendances,
    vocabularyBookAttendances,
    miniStoryAttendances,
    authenticExpressionAttendances,
    studentQuery.data?.data?.createdAt,
  ]);
};
